import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Globe,
  CheckCircle2,
  AlertCircle,
  Clock,
  Send,
  ExternalLink,
  Ban,
  Tag,
  Loader2,
  X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Badge } from 'components/ui/badge';
import { Input } from 'components/ui/input';
import { useToast } from 'components/ui/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import * as TestApi from 'modules/test/api';
import { usePublicCategories } from 'modules/test/hooks';
import type * as Types from 'modules/test/types';
import { cn } from 'lib/utils';

export interface MarketplaceFormData {
  category: string;
  subjectKey?: string;
  variantNumber?: number;
  isLeadVariant?: boolean;
  difficultyLevel: string;
  description: string;
  tags: string[];
  accessType: 'FREE' | 'PAID' | 'PLAN';
  priceSom: number;
}

interface MarketplaceListingCardProps {
  testId: string;
  test: Types.IEntity.Test & Partial<Pick<Types.IEntity.TestDetails, 'questions' | 'examConfig'>>;
  refetch: () => void;
  onMarketplaceDataChange?: (data: MarketplaceFormData) => void;
  onPublishingDenied?: () => void;
}


const DIFFICULTY_OPTIONS = [
  { value: 'beginner', label: 'Boshlang‘ich daraja (Beginner)' },
  { value: 'intermediate', label: "O'rta daraja (Intermediate)" },
  { value: 'advanced', label: 'Yuqori daraja (Advanced)' },
];

export const POPULAR_TAGS = [
  'IELTS',
  'Digital SAT',
  'DTM',
  'Milliy Sertifikat',
  'Attestatsiya',
  'Matematika',
  'Grammar',
  'Reading',
  'Vocabulary',
];
const POPULAR_SUBJECTS = ['Matematika', 'Biologiya', 'Kimyo', 'Fizika', 'Ona tili', 'Tarix', 'Ingliz tili', 'Geografiya', 'Informatika'];

export function parseTestTitleVariant(title: string) {
  const trimmed = (title || '').trim();
  const suffixMatch = trimmed.match(
    /^(.*?)(?:\s*[-–—:]*\s*|\s+)(?:(\d+)\s*[-_]?\s*(?:variant|varinat|v)|(?:variant|varinat|v)\s*[-_]?\s*(\d+))\s*$/i
  );
  if (suffixMatch) {
    const rawBase = suffixMatch[1].trim().replace(/[-–—:]+$/, '').trim();
    const num = parseInt(suffixMatch[2] || suffixMatch[3], 10);
    return { baseTitle: rawBase || trimmed, variantNumber: Number.isFinite(num) ? num : null };
  }
  const prefixMatch = trimmed.match(
    /^(?:(?:variant|varinat|v)\s*[-_]?\s*(\d+)|(\d+)\s*[-_]?\s*(?:variant|varinat|v))\s*[-–—:]*\s*(.*)$/i
  );
  if (prefixMatch) {
    const rawBase = prefixMatch[3].trim().replace(/^[-–—:]+/, '').trim();
    const num = parseInt(prefixMatch[1] || prefixMatch[2], 10);
    return { baseTitle: rawBase || trimmed, variantNumber: Number.isFinite(num) ? num : null };
  }
  return { baseTitle: trimmed, variantNumber: null };
}

export const MarketplaceListingCard: React.FC<MarketplaceListingCardProps> = ({
  testId,
  test,
  refetch,
  onMarketplaceDataChange,
  onPublishingDenied,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const marketplaceStatus = test.marketplaceStatus || 'NONE';
  const isApproved = marketplaceStatus === 'APPROVED';
  const isPending = marketplaceStatus === 'PENDING_REVIEW';
  const isRejected = marketplaceStatus === 'REJECTED';

  const queryClient = useQueryClient();
  const { data: categoriesData } = usePublicCategories();
  const dynamicCategories = useMemo(
    () => (categoriesData?.data || []).filter((c) => c.id !== 'all'),
    [categoriesData?.data]
  );

  const parsedTitle = useMemo(() => parseTestTitleVariant(test.title || ''), [test.title]);

  const [category, setCategory] = useState(test.category || 'general');
  const [subjectKey, setSubjectKey] = useState(test.subjectKey || '');
  const [isCustomSubject, setIsCustomSubject] = useState(false);

  const activeCategory = useMemo(
    () => dynamicCategories.find((c) => c.id === category),
    [dynamicCategories, category]
  );
  const canonicalSubjects = useMemo(() => activeCategory?.subjects || [], [activeCategory]);
  const hasCanonicalSubjects = canonicalSubjects.length > 0;

  const matchedCanonical = useMemo(
    () =>
      canonicalSubjects.find(
        (s) =>
          s.label.toLowerCase() === subjectKey.trim().toLowerCase() ||
          s.key.toLowerCase() === subjectKey.trim().toLowerCase() ||
          s.aliases?.some((a) => a.toLowerCase() === subjectKey.trim().toLowerCase())
      ),
    [canonicalSubjects, subjectKey]
  );

  const [variantNumber, setVariantNumber] = useState<number | undefined>(
    test.variantNumber !== undefined && test.variantNumber !== null
      ? test.variantNumber
      : parsedTitle.variantNumber ?? undefined
  );
  const [isLeadVariant, setIsLeadVariant] = useState(
    test.isLeadVariant !== undefined
      ? Boolean(test.isLeadVariant)
      : parsedTitle.variantNumber === 1
  );
  const [difficultyLevel, setDifficultyLevel] = useState(test.difficultyLevel || 'intermediate');
  const [description, setDescription] = useState(test.description || '');
  const [tags, setTags] = useState<string[]>(test.tags || []);
  const [tagInput, setTagInput] = useState('');
  const [accessType, setAccessType] = useState<'FREE' | 'PAID' | 'PLAN'>(test.accessType || 'FREE');
  const [priceSom, setPriceSom] = useState<number>(test.priceSom || 15000);
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [savingChanges, setSavingChanges] = useState(false);

  // Track test id so we only re-hydrate form state when switching to a different test
  const currentTestIdRef = useRef(test?.id || testId);
  useEffect(() => {
    const currentId = test?.id || testId;
    if (currentTestIdRef.current !== currentId) {
      currentTestIdRef.current = currentId;
      setCategory(test.category || 'general');
      setSubjectKey(test.subjectKey || '');
      setIsCustomSubject(false);
      setVariantNumber(
        test.variantNumber !== undefined && test.variantNumber !== null
          ? test.variantNumber
          : parsedTitle.variantNumber ?? undefined
      );
      setIsLeadVariant(
        test.isLeadVariant !== undefined
          ? Boolean(test.isLeadVariant)
          : parsedTitle.variantNumber === 1
      );
      setDifficultyLevel(test.difficultyLevel || 'intermediate');
      setDescription(test.description || '');
      setTags(test.tags || []);
      setAccessType(test.accessType || 'FREE');
      setPriceSom(test.priceSom || 15000);
    }
  }, [test, testId, parsedTitle]);

  // When canonical subjects become available or change, reconcile subjectKey/custom status without touching other form state
  const autoSuggestedTitleRef = useRef(false);
  useEffect(() => {
    if (canonicalSubjects.length === 0) return;

    if (subjectKey) {
      const isMatch = canonicalSubjects.some(
        (s) =>
          s.label.toLowerCase() === subjectKey.trim().toLowerCase() ||
          s.key.toLowerCase() === subjectKey.trim().toLowerCase() ||
          s.aliases?.some((a) => a.toLowerCase() === subjectKey.trim().toLowerCase())
      );
      setIsCustomSubject(!isMatch);
    } else if (!autoSuggestedTitleRef.current && parsedTitle.baseTitle) {
      const matched = canonicalSubjects.find(
        (s) =>
          s.label.toLowerCase() === parsedTitle.baseTitle.toLowerCase() ||
          s.key.toLowerCase() === parsedTitle.baseTitle.toLowerCase() ||
          s.aliases?.some((a) => a.toLowerCase() === parsedTitle.baseTitle.toLowerCase())
      );
      if (matched) {
        autoSuggestedTitleRef.current = true;
        setSubjectKey(matched.label);
        setIsCustomSubject(false);
      }
    }
  }, [canonicalSubjects, parsedTitle.baseTitle, subjectKey]);

  const handleCategoryChange = (newCategory: string) => {
    setCategory(newCategory);
    const newCat = dynamicCategories.find((c) => c.id === newCategory);
    const newSubjects = newCat?.subjects || [];
    if (newSubjects.length > 0) {
      if (subjectKey.trim()) {
        const isMatch = newSubjects.some(
          (s) =>
            s.label.toLowerCase() === subjectKey.trim().toLowerCase() ||
            s.key.toLowerCase() === subjectKey.trim().toLowerCase() ||
            s.aliases?.some((a) => a.toLowerCase() === subjectKey.trim().toLowerCase())
        );
        setIsCustomSubject(!isMatch);
      } else if (parsedTitle.baseTitle) {
        const matched = newSubjects.find(
          (s) =>
            s.label.toLowerCase() === parsedTitle.baseTitle.toLowerCase() ||
            s.key.toLowerCase() === parsedTitle.baseTitle.toLowerCase() ||
            s.aliases?.some((a) => a.toLowerCase() === parsedTitle.baseTitle.toLowerCase())
        );
        if (matched) {
          setSubjectKey(matched.label);
          setIsCustomSubject(false);
        }
      }
    } else {
      setIsCustomSubject(false);
    }
  };

  useEffect(() => {
    onMarketplaceDataChange?.({
      category,
      subjectKey: subjectKey.trim() || undefined,
      variantNumber: variantNumber !== undefined ? variantNumber : undefined,
      isLeadVariant,
      difficultyLevel,
      description,
      tags,
      accessType,
      priceSom,
    });
  }, [category, subjectKey, variantNumber, isLeadVariant, difficultyLevel, description, tags, accessType, priceSom, onMarketplaceDataChange]);

  const handleSaveMarketplace = async () => {
    if (savingChanges) return;
    setSavingChanges(true);
    try {
      await TestApi.UpdateConfigAccess(testId, { accessType: 'marketplace' });
      await TestApi.Update({
        id: testId,
        category,
        subjectKey: subjectKey.trim() || undefined,
        variantNumber: variantNumber !== undefined ? variantNumber : undefined,
        isLeadVariant,
        difficultyLevel,
        description: description.trim(),
        tags,
        accessType,
        priceSom: accessType === 'PAID' ? priceSom : 0,
      });
      queryClient.invalidateQueries({ queryKey: ['public-test-categories'] });
      toast.success(t('saved', 'Saqlandi'), {
        description: t('marketplace_saved_desc', "Katalog ma'lumotlari muvaffaqiyatli saqlandi."),
      });
      refetch();
    } catch (err: any) {
      if (err?.response?.data?.code === 'MARKETPLACE_NOT_ENABLED') onPublishingDenied?.();
      toast.error(t('error', 'Xatolik'), {
        description: err?.response?.data?.message || "Ma'lumotlarni saqlashda xatolik yuz berdi.",
      });
    } finally {
      setSavingChanges(false);
    }
  };

  const handleAddTag = (tagToAdd: string) => {
    const trimmed = tagToAdd.trim();
    if (!trimmed || tags.length >= 8) return;

    // Smart-match against POPULAR_TAGS (e.g. "milliysertifikat" or "milliy sertifikat" -> "Milliy Sertifikat")
    const collapsedInput = trimmed.toLowerCase().replace(/[-_\s]+/g, '');
    const matchedPopular = POPULAR_TAGS.find(
      (pt) => pt.toLowerCase().replace(/[-_\s]+/g, '') === collapsedInput
    );
    const resolvedTag = matchedPopular || trimmed;

    if (tags.some((t) => t.toLowerCase() === resolvedTag.toLowerCase())) return;

    setTags([...tags, resolvedTag]);
    setTagInput('');
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((t) => t.toLowerCase() !== tagToRemove.toLowerCase()));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddTag(tagInput);
    }
  };

  // Eligibility Checklist
  const isActive = test.status === 'active' || test.status === 'published';
  // A reading passage is one question holding many; count what candidates answer.
  const questionCount = test.questions?.length
    ? test.questions.reduce((n, q) => {
        const item = q as { type?: string; subQuestions?: unknown[] };
        return n + (item.type === 'PASSAGE' ? item.subQuestions?.length ?? 0 : 1);
      }, 0)
    : test.totalQuestions ?? 0;
  // IELTS/SAT tests pass the exam readiness check on activation, and their
  // library-drawn sections hold no questions of their own, so the floor would
  // undercount a full mock. The server applies the same rule.
  const examStandard = test.examConfig?.standard;
  const hasEnoughQuestions = Boolean(examStandard) || questionCount >= 5;
  const hasValidDescription = description.trim().length >= 15;
  const hasEnoughTags = tags.length >= 2;
  const hasValidPrice = accessType !== 'PAID' || priceSom >= 1000;
  const isEligible = isActive && hasEnoughQuestions && hasValidDescription && hasEnoughTags && hasValidPrice;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isEligible || submitting) return;

    if (!category) {
      toast.error('Kategoriya tanlanishi shart');
      return;
    }

    setSubmitting(true);
    try {
      await TestApi.SubmitMarketplace(testId, {
        category,
        subjectKey: subjectKey.trim() || undefined,
        variantNumber: variantNumber !== undefined ? variantNumber : undefined,
        isLeadVariant,
        difficultyLevel,
        description: description.trim(),
        tags,
        accessType,
        priceSom: accessType === 'PAID' ? priceSom : 0,
      });
      queryClient.invalidateQueries({ queryKey: ['public-test-categories'] });
      toast.success(t('marketplace_submitted_title', 'Muvaffaqiyatli yuborildi!'), {
        description: t(
          'marketplace_submitted_desc',
          'Testingiz ko‘rib chiqish uchun yuborildi. Administrator tasdiqlagach, u ommaviy katalogda paydo bo‘ladi.',
        ),
      });
      refetch();
    } catch (err: any) {
      if (err?.response?.data?.code === 'MARKETPLACE_NOT_ENABLED') onPublishingDenied?.();
      toast.error(t('error', 'Xatolik'), {
        description: err?.response?.data?.message || 'Testingizni yuborishda xatolik yuz berdi.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (cancelling) return;
    setCancelling(true);
    try {
      await TestApi.CancelMarketplace(testId);
      toast.success(t('marketplace_cancelled_title', 'Bekor qilindi'), {
        description: t('marketplace_cancelled_desc', 'Test ommaviy bozordan chiqarildi.'),
      });
      refetch();
    } catch (err: any) {
      if (err?.response?.data?.code === 'MARKETPLACE_NOT_ENABLED') onPublishingDenied?.();
      toast.error(t('error', 'Xatolik'), {
        description: err?.response?.data?.message || 'Amalni bajarishda xatolik yuz berdi.',
      });
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Card className="border-border shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground" aria-hidden />
              <CardTitle className="text-base font-bold">
                {t('marketplace_card_title', 'Ommaviy Mock Katalogi (examing.uz/tests)')}
              </CardTitle>
            </div>
            <CardDescription className="text-xs">
              {t(
                'marketplace_card_desc',
                "Testni barcha foydalanuvchilar va qidiruv tizimlari uchun ommaviy katalogda e'lon qilish.",
              )}
            </CardDescription>
          </div>

          <div>
            {isApproved && (
              <Badge variant="success" className="gap-1.5 px-2.5 py-1">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {t('marketplace_status_approved', 'Katalogda faol')}
              </Badge>
            )}
            {isPending && (
              <Badge variant="warning" className="gap-1.5 px-2.5 py-1">
                <Clock className="h-3.5 w-3.5 animate-spin" />
                {t('marketplace_status_pending', 'Tekshirilmoqda')}
              </Badge>
            )}
            {isRejected && (
              <Badge className="bg-rose-500/10 text-rose-700 hover:bg-rose-500/20 border-rose-200 dark:border-rose-800 dark:text-rose-400 gap-1.5 py-1 px-3">
                <AlertCircle className="h-3.5 w-3.5" />
                {t('marketplace_status_rejected', 'Qaytarildi')}
              </Badge>
            )}
            {marketplaceStatus === 'NONE' && (
              <Badge variant="outline" className="text-muted-foreground gap-1.5 py-1 px-3">
                {t('marketplace_status_none', 'Katalogda emas')}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6 pt-0">
        {/* Status Callout Banners */}
        {isApproved && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 rounded-xl border border-success-200 bg-success-50/50 p-4 dark:border-success-900/50 dark:bg-success-900/20">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success-600 dark:text-success-400" />
              <div>
                <h4 className="text-xs font-bold text-success-900 dark:text-success-200">
                  {t('test_live_on_marketplace', 'Ushbu test examing.uz/tests sahifasida ommaga ochiq!')}
                </h4>
                <p className="text-xs text-success-700 dark:text-success-300">
                  {t(`marketplace_${(test.accessType || 'FREE').toLowerCase()}_description`)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 self-end sm:self-auto">
              <a
                href={`https://examing.uz/tests/${testId}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg border border-success-300 bg-white px-3 py-1.5 text-xs font-semibold text-success-900 shadow-sm transition hover:bg-success-50 dark:border-success-800 dark:bg-success-900/40 dark:text-success-200"
              >
                <span>{t('view_on_site', 'Saytda ko‘rish')}</span>
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                disabled={cancelling}
                className="text-xs border-rose-200 text-rose-600 hover:bg-rose-50 dark:border-rose-900 dark:hover:bg-rose-950/40"
              >
                {cancelling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ban className="h-3.5 w-3.5 mr-1" />}
                {t('withdraw', 'Chiqarish')}
              </Button>
            </div>
          </div>
        )}

        {isPending && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 rounded-xl border border-warning-200 bg-warning-50/50 p-4 dark:border-warning-900/50 dark:bg-warning-900/20">
            <div className="flex items-start gap-3">
              <Clock className="mt-0.5 h-5 w-5 shrink-0 text-warning-600 dark:text-warning-400" />
              <div>
                <h4 className="text-xs font-bold text-warning-900 dark:text-warning-200">
                  {t('test_pending_review', 'Test moderatorlar tomonidan ko‘rib chiqilmoqda')}
                </h4>
                <p className="text-xs text-warning-700 dark:text-warning-300">
                  {t('test_pending_desc', 'Tekshiruv odatda bir necha soat ichida amalga oshiriladi. Tasdiqlanishi bilan test katalogga joylashtiriladi.')}
                </p>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={cancelling}
              className="text-xs self-end sm:self-auto"
            >
              {cancelling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              {t('cancel_submission', 'So‘rovni bekor qilish')}
            </Button>
          </div>
        )}

        {isRejected && (
          <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-4 dark:border-rose-900/50 dark:bg-rose-950/20 space-y-2">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600 dark:text-rose-400" />
              <div>
                <h4 className="text-xs font-bold text-rose-900 dark:text-rose-200">
                  {t('submission_rejected_title', 'Afsuski, test ommaviy katalogga qabul qilinmadi')}
                </h4>
                <p className="text-xs font-medium text-rose-800 dark:text-rose-300 mt-1">
                  <strong>{t('moderator_reason', 'Sabab:')}</strong> {test.marketplaceRejectionReason || "Sifat talablariga to'liq javob bermadi."}
                </p>
                <p className="text-[11.5px] text-rose-600 dark:text-rose-400 mt-2">
                  Iltimos, yuqoridagi kamchiliklarni to'g'irlab, qaytadan yuboring.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Live Quality Checklist */}
        <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-2.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-foreground">
              {t('quality_checklist_title', 'Katalogga qo‘shish talablari')}
            </span>
            <span className="text-[11px] text-muted-foreground">
              {isEligible ? (
                <span className="text-success-600 font-bold dark:text-success-400 flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Barcha talablar bajarildi
                </span>
              ) : (
                <span className="text-warning-600 font-semibold dark:text-warning-400">
                  Ayrim talablar to‘ldirilishi kerak
                </span>
              )}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            <div className={`flex items-center gap-2 ${isActive ? 'text-success-600 dark:text-success-400' : 'text-muted-foreground'}`}>
              {isActive ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <AlertCircle className="h-4 w-4 shrink-0" />}
              <span>Test faollashtirilgan (ACTIVE)</span>
            </div>
            <div className={`flex items-center gap-2 ${hasEnoughQuestions ? 'text-success-600 dark:text-success-400' : 'text-muted-foreground'}`}>
              {hasEnoughQuestions ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <AlertCircle className="h-4 w-4 shrink-0" />}
              <span>
                {examStandard
                  ? `To‘liq ${examStandard} imtihoni: barcha bo‘limlar tekshirilgan`
                  : `Kamida 5 ta savol mavjud (${questionCount}/5)`}
              </span>
            </div>
            <div className={`flex items-center gap-2 ${hasValidDescription ? 'text-success-600 dark:text-success-400' : 'text-muted-foreground'}`}>
              {hasValidDescription ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <AlertCircle className="h-4 w-4 shrink-0" />}
              <span>Batafsil tavsif ({description.trim().length}/15 belgi)</span>
            </div>
            <div className={`flex items-center gap-2 ${hasEnoughTags ? 'text-success-600 dark:text-success-400' : 'text-muted-foreground'}`}>
              {hasEnoughTags ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <AlertCircle className="h-4 w-4 shrink-0" />}
              <span>Kamida 2 ta qidiruv tegi ({tags.length}/2)</span>
            </div>
            <div className={`flex items-center gap-2 ${hasValidPrice ? 'text-success-600 dark:text-success-400' : 'text-muted-foreground'}`}>
              {hasValidPrice ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <AlertCircle className="h-4 w-4 shrink-0" />}
              <span>
                {accessType === 'PLAN' ? t('marketplace_plan_description') : accessType === 'FREE'
                  ? "Narx: Bepul test (0 so'm)"
                  : `Narx: ${priceSom.toLocaleString('uz-UZ')} so'm (min 1 000)`}
              </span>
            </div>
          </div>
        </div>

        {/* Metadata Configuration Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1">
                {t('category', 'Kategoriya')} *
              </label>
              <Select value={category} onValueChange={handleCategoryChange} disabled={isPending}>
                <SelectTrigger className="w-full text-xs font-medium">
                  <SelectValue placeholder={t('select_category', 'Toifani tanlang')} />
                </SelectTrigger>
                <SelectContent>
                  {dynamicCategories.length === 0 ? (
                    <SelectItem value="__empty" disabled>Toifalar yuklanmoqda...</SelectItem>
                  ) : (
                    dynamicCategories.map((opt) => (
                      <SelectItem key={opt.id} value={opt.id}>
                        {opt.label}
                      </SelectItem>
                    ))
                  )}
                  {category && !dynamicCategories.some((c) => c.id === category) && (
                    <SelectItem value={category}>{category}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground mb-1">
                {t('difficulty_level', 'Qiyinlik darajasi')} *
              </label>
              <Select value={difficultyLevel} onValueChange={setDifficultyLevel} disabled={isPending}>
                <SelectTrigger className="w-full text-xs font-medium">
                  <SelectValue placeholder={t('select_difficulty', 'Qiyinlik darajasini tanlang')} />
                </SelectTrigger>
                <SelectContent>
                  {DIFFICULTY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Subject & Variant Ordering */}
          <div className="rounded-xl border border-border/70 bg-muted/10 p-3.5 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">
                  {t('subject_key', 'Fan / Yo‘nalish')}
                </label>
                {hasCanonicalSubjects && !isCustomSubject ? (
                  <div className="space-y-1">
                    <Select
                      value={matchedCanonical ? matchedCanonical.key : ''}
                      onValueChange={(val) => {
                        if (val === '__custom') {
                          setIsCustomSubject(true);
                          return;
                        }
                        const found = canonicalSubjects.find((s) => s.key === val);
                        if (found) {
                          setSubjectKey(found.label);
                          setIsCustomSubject(false);
                        }
                      }}
                      disabled={isPending}
                    >
                      <SelectTrigger className="w-full text-xs font-medium bg-background">
                        <SelectValue placeholder="Fanni tanlang..." />
                      </SelectTrigger>
                      <SelectContent>
                        {canonicalSubjects.map((cs) => (
                          <SelectItem key={cs.key} value={cs.key}>
                            {cs.label}
                          </SelectItem>
                        ))}
                        <SelectItem value="__custom" className="font-medium">
                          + Boshqa fan kiritish...
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <Input
                      type="text"
                      placeholder="masalan: Matematika, Biologiya..."
                      value={subjectKey}
                      onChange={(e) => setSubjectKey(e.target.value)}
                      disabled={isPending}
                      className="text-xs font-medium bg-background"
                    />
                    {hasCanonicalSubjects && (
                      <button
                        type="button"
                        onClick={() => {
                          setIsCustomSubject(false);
                          if (canonicalSubjects.length > 0 && !matchedCanonical) {
                            setSubjectKey(canonicalSubjects[0].label);
                          }
                        }}
                        className="block text-[10.5px] font-medium text-foreground underline-offset-2 hover:underline"
                      >
                        ← Kanonik fanlar ro'yxatidan tanlash
                      </button>
                    )}
                  </div>
                )}
                <span className="text-[10.5px] text-muted-foreground mt-1 block">
                  Variantlarni bitta fan kartasiga jamlash uchun
                </span>
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">
                  {t('variant_number', 'Variant raqami')}
                </label>
                <Input
                  type="number"
                  min={1}
                  max={100}
                  placeholder="1, 2, 3..."
                  value={variantNumber ?? ''}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    setVariantNumber(isNaN(val) ? undefined : val);
                  }}
                  disabled={isPending}
                  className="text-xs font-medium bg-background"
                />
                <span className="text-[10.5px] text-muted-foreground mt-1 block">
                  Tartib raqami (1-Variant, 2-Variant...)
                </span>
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">
                  {t('lead_variant', 'Asosiy variant')}
                </label>
                <label className="flex items-center gap-2.5 rounded-md border border-input bg-background p-2 cursor-pointer hover:bg-muted/30 transition text-xs font-medium min-h-[38px]">
                  <input
                    type="checkbox"
                    checked={isLeadVariant}
                    onChange={(e) => setIsLeadVariant(e.target.checked)}
                    disabled={isPending}
                    className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                  />
                  <span className="text-foreground text-xs leading-tight">
                    Bosh sahifada ko‘rsatilsin
                  </span>
                </label>
                <span className="text-[10.5px] text-muted-foreground mt-1 block">
                  Ushbu fanning 1-bepul / asosiy varianti
                </span>
              </div>
            </div>

            {!isPending && (
              <div className="flex flex-wrap items-center gap-1.5 pt-1 border-t border-border/50">
                <span className="text-[10.5px] text-muted-foreground">
                  {hasCanonicalSubjects ? "Toifadagi fanlar:" : "Tezkor tanlash:"}
                </span>
                {(hasCanonicalSubjects ? canonicalSubjects.map((s) => s.label) : POPULAR_SUBJECTS).map((ps) => (
                  <button
                    key={ps}
                    type="button"
                    onClick={() => {
                      setSubjectKey(ps);
                      const isMatch = canonicalSubjects.some(
                        (s) => s.label.toLowerCase() === ps.toLowerCase() || s.key.toLowerCase() === ps.toLowerCase()
                      );
                      setIsCustomSubject(!isMatch && hasCanonicalSubjects);
                    }}
                    className={cn(
                      'text-[10.5px] rounded-full border px-2 py-0.5 transition font-medium',
                      (matchedCanonical && matchedCanonical.label.toLowerCase() === ps.toLowerCase()) ||
                      subjectKey.toLowerCase() === ps.toLowerCase()
                        ? 'border-foreground bg-foreground font-semibold text-background'
                        : 'border-border bg-background text-muted-foreground hover:border-foreground/30 hover:text-foreground'
                    )}
                  >
                    {ps}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-semibold text-foreground">
                {t('description', 'Test tavsifi (talabalar uchun)')} *
              </label>
              <span className={`text-[11px] ${description.length >= 15 ? 'text-success-600 dark:text-success-400' : 'text-muted-foreground'}`}>
                {description.length} / 15 min
              </span>
            </div>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isPending}
              placeholder="Ushbu test kimlar uchun, qanday mavzularni qamrab oladi va imtihon formati haqida qisqacha ma'lumot..."
              className="w-full rounded-md border border-input bg-background p-3 text-xs focus-visible:!outline-none focus-visible:ring-2 focus-visible:ring-ring resize-y"
            />
          </div>

          {/* Pricing & Access Type */}
          <div className="space-y-2.5">
            <label className="block text-xs font-semibold text-foreground">
              {t('pricing_and_access', 'Kirish turi va Narx (UZS)')} *
            </label>

            <div role="group" aria-label={t('pricing_and_access')} className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {(['FREE', 'PAID', 'PLAN'] as const).map((option) => (
                <label
                  key={option}
                  className={cn(
                    'group flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-all focus-within:ring-2 focus-within:ring-ring',
                    accessType === option
                      ? 'border-foreground ring-1 ring-foreground'
                      : 'border-border bg-card hover:border-neutral-300 dark:hover:border-neutral-700 hover:bg-muted/20',
                    isPending && 'cursor-not-allowed opacity-60'
                  )}
                >
                  <input
                    type="radio"
                    name="marketplace-pricing"
                    value={option}
                    checked={accessType === option}
                    onChange={() => setAccessType(option)}
                    disabled={isPending}
                    className="sr-only"
                  />
                  <span
                    className={cn(
                      'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-all',
                      accessType === option
                        ? 'border-foreground bg-background'
                        : 'border-input bg-background group-hover:border-muted-foreground/60'
                    )}
                    aria-hidden="true"
                  >
                    {accessType === option && (
                      <span className="h-2 w-2 rounded-full bg-foreground" />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs font-bold text-foreground">{t(`marketplace_${option.toLowerCase()}_label`)}</span>
                    <span className="mt-0.5 block text-[11px] text-muted-foreground">{t(`marketplace_${option.toLowerCase()}_description`)}</span>
                  </span>
                </label>
              ))}
            </div>

            {/* Price input when PAID */}
            {accessType === 'PAID' && (
              <div className="mt-2 space-y-2 rounded-lg border border-border bg-background p-3.5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <label className="text-xs font-semibold text-foreground">
                    Test narxi (so'mda):
                  </label>
                  <span className="text-[11px] text-muted-foreground font-medium">
                    Minimal narx: 1 000 so'm
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative flex-1 min-w-[160px] max-w-xs">
                    <Input
                      type="number"
                      step={1000}
                      min={1000}
                      value={priceSom || ''}
                      onChange={(e) => setPriceSom(Math.max(0, parseInt(e.target.value, 10) || 0))}
                      disabled={isPending}
                      placeholder="25000"
                      className="pr-14 font-semibold text-xs text-foreground"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-semibold pointer-events-none">
                      so'm
                    </span>
                  </div>

                  {/* Presets */}
                  <div className="flex flex-wrap gap-1 items-center">
                    {[10000, 25000, 50000, 100000].map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => !isPending && setPriceSom(preset)}
                        className={`rounded-md border px-2.5 py-1 text-[11px] font-medium transition ${
                          priceSom === preset
                            ? 'border-foreground bg-foreground text-background'
                            : 'border-border bg-background hover:bg-muted text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {preset.toLocaleString('uz-UZ')}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Tags */}
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1">
              {t('tags', 'Qidiruv teglari (kamida 2 ta)')} *
            </label>
            <div className="flex flex-wrap gap-1.5 p-2 rounded-md border border-input bg-background min-h-[42px] items-center focus-within:ring-2 focus-within:ring-ring">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs font-medium text-foreground"
                >
                  <Tag className="h-3 w-3 text-muted-foreground" />
                  {tag}
                  {!isPending && (
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </span>
              ))}

              {!isPending && tags.length < 8 && (
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  placeholder={tags.length === 0 ? "Teg yozing va Enter bosing..." : "Yana teg..."}
                  className="flex-1 bg-transparent px-2 py-0.5 text-xs outline-none focus-visible:!outline-none min-w-[120px]"
                />
              )}
            </div>

            {!isPending && (
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                <span className="text-[11px] text-muted-foreground">Tavsiya etilgan:</span>
                {POPULAR_TAGS.filter(
                  (pt) => !tags.some((t) => t.toLowerCase().replace(/[-_\s]+/g, '') === pt.toLowerCase().replace(/[-_\s]+/g, ''))
                ).slice(0, 8).map((pt) => (
                  <button
                    key={pt}
                    type="button"
                    onClick={() => handleAddTag(pt)}
                    className="text-[11px] rounded-full border border-border px-2 py-0.5 text-muted-foreground hover:border-foreground/30 hover:text-foreground transition"
                  >
                    + {pt}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Action buttons */}
          {isApproved && (
            <div className="flex justify-end pt-2">
              <Button
                type="button"
                onClick={handleSaveMarketplace}
                disabled={savingChanges}
                className="gap-2 text-xs"
              >
                {savingChanges ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" />
                )}
                <span>{t('save_changes', "O'zgarishlarni saqlash")}</span>
              </Button>
            </div>
          )}

          {!isApproved && !isPending && (
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleSaveMarketplace}
                disabled={savingChanges}
                className="text-xs"
              >
                {savingChanges ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
                <span>{t('save_draft_data', "Qoralamani saqlash")}</span>
              </Button>
              <Button
                type="submit"
                disabled={!isEligible || submitting}
                className="gap-2 text-xs"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                <span>
                  {isRejected
                    ? t('resubmit_to_marketplace', 'Qaytadan ko‘rib chiqishga yuborish')
                    : t('submit_to_marketplace', 'Ommaviy katalogga yuborish')}
                </span>
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};

export default MarketplaceListingCard;
